import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../library/utilities/intl.js';
import styles from './shipment-list.module.scss';

// One row per delivery address, as a compact timeline. The order manager ships
// and confirms each address separately, so a multi-address order can be partly
// shipped. Data comes from `item.shipments` (client item API).
const ShipmentList = ({ shipments = [], title }) => {
    const { t } = useTranslation()
    if (!shipments.length) {
        return null
    }

    const isMulti = shipments.length > 1
    const deliveredCount = shipments.filter(({ status }) => status === 'delivered').length
    const statusLabel = {
        pending: t('Preparing shipment'),
        in_transit: t('In Transit ').trim(),
        delivered: t('Delivered'),
    }

    return (
        <section className={styles.wrapper}>
            {(!!title || isMulti) && (
                <header className={styles.top}>
                    {!!title && <h4 className={styles.title}>{title}</h4>}
                    {isMulti && <span className={styles.summary}>{deliveredCount} / {shipments.length} {t('Delivered')}</span>}
                </header>
            )}
            <ol className={styles.timeline}>
                {shipments.map((shipment, index) => {
                    // older API responses only have the joined `address` string
                    const lines = shipment.address_lines?.length ? shipment.address_lines : [shipment.address].filter(Boolean)
                    return (
                        <li key={shipment.id} className={`${styles.row} ${styles[shipment.status] ?? ''}`}>
                            <span className={styles.dot} aria-hidden="true" />
                            <div className={styles.body}>
                                <div className={styles.head}>
                                    <strong className={styles.name}>
                                        {isMulti ? `${t('Delivery')} ${index + 1} / ${shipments.length}` : t('Delivery')}
                                    </strong>
                                    <span className={styles.badge}>{statusLabel[shipment.status]}</span>
                                </div>
                                <div className={styles.meta}>
                                    <span>{t('Quantity')}: <b>{shipment.ship_quantity ?? shipment.quantity}</b></span>
                                    {shipment.status === 'in_transit' && !!shipment.expected_date && (
                                        <span>{t('Expected Delivery')}: <b>{formatDate(shipment.expected_date)}</b></span>
                                    )}
                                </div>
                                {lines.length > 0 && (
                                    <address className={styles.address}>
                                        {lines.map((line, i) => <span key={i} className={i === 0 && lines.length > 1 ? styles.company : undefined}>{line}</span>)}
                                    </address>
                                )}
                                {shipment.status === 'in_transit' && !!shipment.tracking_url && (
                                    <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer" className={styles.track}>
                                        {t('Track shipment')} <span aria-hidden="true">→</span>
                                    </a>
                                )}
                            </div>
                        </li>
                    )
                })}
            </ol>
        </section>
    )
}

ShipmentList.propTypes = {
    shipments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        quantity: PropTypes.number,
        ship_quantity: PropTypes.number,
        address: PropTypes.string,
        address_lines: PropTypes.arrayOf(PropTypes.string),
        status: PropTypes.oneOf(['pending', 'in_transit', 'delivered']),
        expected_date: PropTypes.string,
        tracking_url: PropTypes.string,
    })),
    title: PropTypes.string,
}

export default ShipmentList
